import { makeAutoObservable, runInAction } from 'mobx';
import { BACKEND_MODULE_TYPES, EXCHANGE_AMOUNT_DIRECTION } from 'components/common/constants';
import { setTimerDurationExpiresAt, updateConfirmationActionType } from 'services/authUtils';
import {
  createExchangeQuoteRequest,
  calculateExchangeDataRequest,
  createExchangeTransferRequest,
  confirmExchangeTransferRequest,
  resendSecurityCode,
  forceChangeConfirmationTypeRequest
} from 'services/requestAgent';
import i18n from '../i18n';

class CurrencyExchangeStore {
  isRepeatTransaction = false;
  isRedirectFromPaymentForm = false;
  isExchangeLoading = false;
  isCalculatedFromToValueAndCommission = false;
  isExchangeTransferCreated = false;
  isExchangeSucceeded = false;
  isAmountExceeded = false;
  isShowSwitchConfirmationMethodScreen = false;
  error = null;
  confirmationPopupError = null;
  temporaryError = null;
  accountFrom = null;
  accountTo = null;
  amountFrom = null;
  amountFromDetails = null;
  amountTo = null;
  amountToDetails = null;
  exchangeData = {
    quoteId: null,
    rate: null,
    rateDetails: null,
    commission: null,
    commissionDetails: null,
    expiresAt: null,
    duration: null
  };
  createdExchangeTransferData = {
    confirmationId: null,
    rate: null,
    rateDetails: null,
    commission: null,
    commissionDetails: null,
    accountFrom: null,
    accountTo: null,
    amountFrom: null,
    amountFromDetails: null,
    amountTo: null,
    amountToDetails: null
  };
  fieldsAreDisabled = false;

  constructor() {
    makeAutoObservable(this);
  }

  resetExchangeStore() {
    this.isRepeatTransaction = false;
    this.isRedirectFromPaymentForm = false;
    this.isExchangeLoading = false;
    this.isCalculatedFromToValueAndCommission = false;
    this.isExchangeTransferCreated = false;
    this.isExchangeSucceeded = false;
    this.isAmountExceeded = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.error = null;
    this.confirmationPopupError = null;
    this.accountFrom = null;
    this.accountTo = null;
    this.amountFrom = null;
    this.amountFromDetails = null;
    this.amountTo = null;
    this.amountToDetails = null;
    this.exchangeData = {
      quoteId: null,
      rate: null,
      rateDetails: null,
      commission: null,
      commissionDetails: null,
      expiresAt: null,
      duration: null
    };
    this.createdExchangeTransferData = {
      confirmationId: null,
      rate: null,
      rateDetails: null,
      commission: null,
      commissionDetails: null,
      accountFrom: null,
      accountTo: null,
      amountFrom: null,
      amountFromDetails: null,
      amountTo: null,
      amountToDetails: null
    };
  }

  setIsRepeatTransactionStatus(status) {
    this.isRepeatTransaction = status;
  }

  setIsRedirectFromPaymentFormStatus(status) {
    this.isRedirectFromPaymentForm = status;
  }

  setIsShowSwitchConfirmationMethodScreen(status) {
    this.isShowSwitchConfirmationMethodScreen = status;
  }

  setFromToAccounts(from, to) {
    this.accountFrom = from;
    this.accountTo = to;
    this.error = null;
    this.fieldsAreDisabled = false;
  }

  setOppositeAmountToZero(amountDirection) {
    this[amountDirection] = '0';
    this.isCalculatedFromToValueAndCommission = false;
    this.exchangeData = {
      ...this.exchangeData,
      commission: null,
      commissionDetails: null
    };
  }

  setAmount(amountType, value) {
    this[amountType] = value;

    if (Number(value) === 0) {
      if (amountType === EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM) {
        this.setOppositeAmountToZero(EXCHANGE_AMOUNT_DIRECTION.AMOUNT_TO);
      } else {
        this.setOppositeAmountToZero(EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM);
      }
    }
  }

  setIsExchangeLoading(status) {
    this.isExchangeLoading = status;
    this.error = null;
    this.fieldsAreDisabled = false;
  }

  resetAfterSuccessfulExchange() {
    this.confirmationPopupError = null;
    this.temporaryError = null;
    this.amountFrom = null;
    this.amountFromDetails = null;
    this.amountTo = null;
    this.amountToDetails = null;
    this.isExchangeSucceeded = false;
    this.isAmountExceeded = false;
    this.createdExchangeTransferData = {
      confirmationId: null,
      rate: null,
      rateDetails: null,
      commission: null,
      commissionDetails: null,
      accountFrom: null,
      accountTo: null,
      amountFrom: null,
      amountFromDetails: null,
      amountTo: null,
      amountToDetails: null
    };
  }

  resetCreatedExchangeTransfer() {
    this.isExchangeTransferCreated = false;
    this.isAmountExceeded = false;
    this.isShowSwitchConfirmationMethodScreen = false;
    this.createdExchangeTransferData = {
      confirmationId: null,
      rate: null,
      rateDetails: null,
      commission: null,
      commissionDetails: null,
      accountFrom: null,
      accountTo: null,
      amountFrom: null,
      amountFromDetails: null,
      amountTo: null,
      amountToDetails: null
    };
  }

  clearConfirmationPopupError() {
    this.confirmationPopupError = null;
  }

  async createExchangeQuote(customerNumber) {
    try {
      this.setIsExchangeLoading(true);

      const {
        quote_id: quoteId,
        rate,
        rateDetails,
        expiresAt,
        duration
      } = await createExchangeQuoteRequest(customerNumber, {
        wallet_from: this.accountFrom?.wallet_number,
        wallet_to: this.accountTo?.wallet_number
      });

      runInAction(() => {
        this.exchangeData = {
          ...this.exchangeData,
          quoteId,
          duration,
          rate: rateDetails?.rate || rate,
          rateDetails,
          expiresAt
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        if (
          err.code === 'EXCHANGE_PAIR_DISABLED' ||
          err.code === 'EXCHANGE_RATE_NOT_FOUND' ||
          err.code === 'PAYMENT_CREATION_TIME_EXPIRED' ||
          err.code === 'OPERATION_NOT_SUPPORTED' ||
          (err?.code === 'NOT_FOUND' && err?.message?.includes('Exchange rate pair'))
        ) {
          this.fieldsAreDisabled = true;
        }
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async calculateExchangeData(customerNumber, amount, direction) {
    try {
      this.setIsExchangeLoading(true);

      const {
        from: amountFrom,
        to: amountTo,
        fromAmountDetails,
        toAmountDetails,
        commission,
        commissionAmountDetails,
        amountExceeded
      } = await calculateExchangeDataRequest(customerNumber, {
        quote_id: this.exchangeData.quoteId,
        amount,
        direction: direction === EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM ? 'AMOUNT_FROM' : 'AMOUNT_TO'
      });

      runInAction(() => {
        if (direction === EXCHANGE_AMOUNT_DIRECTION.AMOUNT_FROM) {
          this.amountTo = toAmountDetails?.amount || amountTo;
          this.amountToDetails = toAmountDetails;
          this.amountFromDetails = fromAmountDetails;
        } else {
          this.amountFrom = fromAmountDetails?.amount || amountFrom;
          this.amountFromDetails = fromAmountDetails;
          this.amountToDetails = toAmountDetails;
        }
        this.exchangeData = {
          ...this.exchangeData,
          commission: commissionAmountDetails?.amount || commission,
          commissionDetails: commissionAmountDetails
        };
        this.isAmountExceeded = amountExceeded;
        this.isCalculatedFromToValueAndCommission = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isCalculatedFromToValueAndCommission = false;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async createExchangeTransfer(customerNumber) {
    try {
      this.setIsExchangeLoading(true);

      const {
        confirmation_id: confirmationId,
        confirmation_type: confirmationType,
        expires_at: expiresAt
      } = await createExchangeTransferRequest(customerNumber, {
        quote_id: this.exchangeData.quoteId,
        amount: this.amountFrom
      });

      setTimerDurationExpiresAt(expiresAt);
      updateConfirmationActionType(confirmationType);

      runInAction(() => {
        this.createdExchangeTransferData = {
          confirmationId: confirmationId,
          rate: this.exchangeData.rate,
          rateDetails: this.exchangeData.rateDetails,
          commission: this.exchangeData.commission,
          commissionDetails: this.exchangeData.commissionDetails,
          accountFrom: this.accountFrom,
          accountTo: this.accountTo,
          amountFrom: this.amountFrom,
          amountFromDetails: this.amountFromDetails,
          amountTo: this.amountTo,
          amountToDetails: this.amountToDetails
        };
        this.isExchangeTransferCreated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
        this.isExchangeTransferCreated = false;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async confirmExchangeTransfer(customerNumber, code) {
    try {
      this.setIsExchangeLoading(true);

      await confirmExchangeTransferRequest(customerNumber, {
        confirmation_id: this.createdExchangeTransferData.confirmationId,
        code
      });

      runInAction(() => {
        this.isExchangeTransferCreated = false;
        this.isExchangeSucceeded = true;
      });
    } catch (err) {
      runInAction(() => {
        // Handle push notification rejection
        if (err?.code === 'CONFIRMATION_REJECTED') {
          this.confirmationPopupError = {
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage
              ? i18n.getMessage('error.confirmationRejected')
              : 'Push notification was rejected. Please try again.'
          };
          this.isExchangeTransferCreated = false;
          // Reset to exchange form state
          this.createdExchangeTransferData.confirmationId = null;
          // Set temporary error that won't block the Exchange button
          this.temporaryError = {
            code: 'CONFIRMATION_REJECTED',
            message: i18n.getMessage
              ? i18n.getMessage('error.confirmationRejected')
              : 'Push notification was rejected. Please try again.'
          };
          // Clear the temporary error after a few seconds
          setTimeout(() => {
            runInAction(() => {
              if (this.temporaryError?.code === 'CONFIRMATION_REJECTED') {
                this.temporaryError = null;
              }
              if (this.confirmationPopupError?.code === 'CONFIRMATION_REJECTED') {
                this.confirmationPopupError = null;
              }
            });
          }, 5000); // Clear after 5 seconds
        } else if (err?.code !== 'CONFIRMATION_PENDING_ACTION') {
          // Don't update error for CONFIRMATION_PENDING_ACTION - just continue polling
          this.confirmationPopupError = err;
        }
        this.isExchangeSucceeded = false;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  async resendExchangeSecurityCode() {
    try {
      this.setIsExchangeLoading(false);
      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await resendSecurityCode(BACKEND_MODULE_TYPES.PAYMENT, this.createdExchangeTransferData.confirmationId);

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.createdExchangeTransferData.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isExchangeLoading = false;
      });
    }
  }

  forceSwitchToAnotherConfirmationType = async (confirmationType) => {
    try {
      runInAction(() => {
        this.isLoading = true;
      });

      const {
        confirmation_id: confirmationId,
        confirmation_type: type,
        expires_at: expiresAt
      } = await forceChangeConfirmationTypeRequest(
        this.createdExchangeTransferData.confirmationId,
        confirmationType,
        BACKEND_MODULE_TYPES.PAYMENT
      );

      updateConfirmationActionType(type);
      setTimerDurationExpiresAt(expiresAt);

      runInAction(() => {
        this.createdExchangeTransferData.confirmationId = confirmationId;
        this.confirmationPopupError = null;
      });
    } catch (err) {
      runInAction(() => {
        this.confirmationPopupError = err;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isShowSwitchConfirmationMethodScreen = false;
      });
    }
  };
}

export default new CurrencyExchangeStore();
